// src/components/dashboard/FinancialSummary.js
import React, { useState, useEffect } from 'react';
import { reportsService, aiService } from '../../services/api';
import { toast } from 'react-toastify';
import {
    CurrencyDollarIcon,
    ChartBarIcon,
    ArrowTrendingUpIcon,
    SparklesIcon,
} from '@heroicons/react/24/outline';

const FinancialSummary = () => {
    const [loading, setLoading] = useState(true);
    const [currentMonthData, setCurrentMonthData] = useState(null);
    const [lastMonthData, setLastMonthData] = useState(null);
    const [ytdData, setYtdData] = useState(null);
    const [aiInsight, setAiInsight] = useState('');
    const [insightLoading, setInsightLoading] = useState(false);

    useEffect(() => {
        fetchFinancialData();
    }, []);

    const fetchFinancialData = async () => {
        try {
            setLoading(true);

            // Calculate date ranges
            const now = new Date();
            const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
            const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
            const today = new Date();

            // Fetch current month's data (month-to-date)
            const currentMonthResponse = await reportsService.financialSummary({
                start_date: firstDayOfCurrentMonth.toISOString().split('T')[0],
                end_date: today.toISOString().split('T')[0],
            });

            // Fetch last month's data
            const lastMonthResponse = await reportsService.financialSummary({
                start_date: firstDayOfLastMonth.toISOString().split('T')[0],
                end_date: lastDayOfLastMonth.toISOString().split('T')[0],
            });

            // Fetch YTD data
            const ytdResponse = await reportsService.financialSummary({
                start_date: firstDayOfYear.toISOString().split('T')[0],
                end_date: today.toISOString().split('T')[0],
            });

            if (currentMonthResponse.success && lastMonthResponse.success && ytdResponse.success) {
                setCurrentMonthData(currentMonthResponse.data.summary);
                setLastMonthData(lastMonthResponse.data.summary);
                setYtdData(ytdResponse.data.summary);

                // Get AI insights
                await generateAIInsight(
                    currentMonthResponse.data.summary,
                    lastMonthResponse.data.summary,
                    ytdResponse.data.summary
                );
            }
        } catch (error) {
            console.error('Error fetching financial data:', error);
            toast.error('Failed to load financial summary');
        } finally {
            setLoading(false);
        }
    };

    const generateAIInsight = async (currentMonth, lastMonth, ytd) => {
        try {
            setInsightLoading(true);

            // Format the numbers for the prompt
            const currentMonthRevenue = formatCurrency(currentMonth.revenue);
            const currentMonthProfit = formatCurrency(currentMonth.gross_profit);
            const currentMonthMargin = formatPercentage(currentMonth.gross_margin);
            const lastMonthRevenue = formatCurrency(lastMonth.revenue);
            const lastMonthProfit = formatCurrency(lastMonth.gross_profit);
            const lastMonthMargin = formatPercentage(lastMonth.gross_margin);
            const ytdRevenue = formatCurrency(ytd.revenue);
            const ytdProfit = formatCurrency(ytd.gross_profit);
            const ytdMargin = formatPercentage(ytd.gross_margin);

            const prompt = `Provide a motivational insight (3-4 sentences max) for the team based on these financial metrics:

CURRENT MONTH (Month-to-Date):
- Revenue: ${currentMonthRevenue}
- Gross Profit: ${currentMonthProfit}
- Gross Margin: ${currentMonthMargin}

LAST MONTH (Complete):
- Revenue: ${lastMonthRevenue}
- Gross Profit: ${lastMonthProfit}
- Gross Margin: ${lastMonthMargin}

YEAR-TO-DATE:
- Revenue: ${ytdRevenue}
- Gross Profit: ${ytdProfit}
- Gross Margin: ${ytdMargin}

IMPORTANT: Use ONLY these exact numbers in your response. Do not make up or hallucinate different numbers. Be encouraging and highlight positive trends or areas of opportunity. You are a financial analyst and these numbers show the companies performance.`;

            const response = await aiService.generateInsight(prompt);

            if (response.success) {
                setAiInsight(response.insight || 'Keep up the great work! Your efforts are driving the business forward.');
            } else {
                setAiInsight('Keep up the great work! Your efforts are driving the business forward.');
            }
        } catch (error) {
            console.error('Error generating AI insight:', error);
            setAiInsight('Keep up the great work! Your efforts are driving the business forward.');
        } finally {
            setInsightLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value || 0);
    };

    const formatPercentage = (value) => {
        return `${(value || 0).toFixed(1)}%`;
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-9">
                        {[...Array(9)].map((_, i) => (
                            <div key={i} className="h-20 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow">
            <div className="p-5 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Financial Summary</h2>
            </div>

            {/* Financial Stats */}
            <div className="p-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-9">
                    {/* Current Month Revenue */}
                    <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CurrencyDollarIcon className="h-6 w-6 text-cyan-600" />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                                <dl>
                                    <dt className="text-xs font-medium text-cyan-600 ">This Month (MTD)</dt>
                                    <dd className="text-lg font-semibold text-cyan-900">
                                        {formatCurrency(currentMonthData?.revenue)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* Current Month Profit */}
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ChartBarIcon className="h-6 w-6 text-emerald-600" />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                                <dl>
                                    <dt className="text-xs font-medium text-emerald-600 ">This Month Profit</dt>
                                    <dd className="text-lg font-semibold text-emerald-900">
                                        {formatCurrency(currentMonthData?.gross_profit)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* Current Month Margin */}
                    <div className="bg-gradient-to-br from-lime-50 to-lime-100 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ArrowTrendingUpIcon className="h-6 w-6 text-lime-600" />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                                <dl>
                                    <dt className="text-xs font-medium text-lime-600 ">This Month Margin</dt>
                                    <dd className="text-lg font-semibold text-lime-900">
                                        {formatPercentage(currentMonthData?.gross_margin)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* Last Month Revenue */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                                <dl>
                                    <dt className="text-xs font-medium text-blue-600 ">Last Month (Full)</dt>
                                    <dd className="text-lg font-semibold text-blue-900">
                                        {formatCurrency(lastMonthData?.revenue)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* Last Month Profit */}
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ChartBarIcon className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                                <dl>
                                    <dt className="text-xs font-medium text-green-600 ">Last Month Profit</dt>
                                    <dd className="text-lg font-semibold text-green-900">
                                        {formatCurrency(lastMonthData?.gross_profit)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* Last Month Margin */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ArrowTrendingUpIcon className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                                <dl>
                                    <dt className="text-xs font-medium text-purple-600 ">Last Month Margin</dt>
                                    <dd className="text-lg font-semibold text-purple-900">
                                        {formatPercentage(lastMonthData?.gross_margin)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* YTD Revenue */}
                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CurrencyDollarIcon className="h-6 w-6 text-indigo-600" />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                                <dl>
                                    <dt className="text-xs font-medium text-indigo-600 ">YTD Revenue</dt>
                                    <dd className="text-lg font-semibold text-indigo-900">
                                        {formatCurrency(ytdData?.revenue)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* YTD Profit */}
                    <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ChartBarIcon className="h-6 w-6 text-teal-600" />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                                <dl>
                                    <dt className="text-xs font-medium text-teal-600 ">YTD Profit</dt>
                                    <dd className="text-lg font-semibold text-teal-900">
                                        {formatCurrency(ytdData?.gross_profit)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* YTD Margin */}
                    <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ArrowTrendingUpIcon className="h-6 w-6 text-pink-600" />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                                <dl>
                                    <dt className="text-xs font-medium text-pink-600 ">YTD Margin</dt>
                                    <dd className="text-lg font-semibold text-pink-900">
                                        {formatPercentage(ytdData?.gross_margin)}
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>

                {/* AI Insight */}
                <div className="mt-6 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-4 border border-primary-200">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <SparklesIcon className="h-6 w-6 text-primary-600" />
                        </div>
                        <div className="ml-3 flex-1">
                            <h3 className="text-sm font-medium text-primary-900 mb-1">AI Financial Insight</h3>
                            {insightLoading ? (
                                <div className="animate-pulse">
                                    <div className="h-4 bg-primary-200 rounded w-3/4 mb-2"></div>
                                    <div className="h-4 bg-primary-200 rounded w-1/2"></div>
                                </div>
                            ) : (
                                <p className="text-sm text-primary-700 leading-relaxed">{aiInsight}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinancialSummary;
